import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { mapVirtualToActualDocumentType } from 'services/documentTypes/documentTypes';
import { BackendError } from 'support/helpers/errors/errors';
import { routeToCreatePage } from 'support/helpers/navigation/navigation';
import { useOCRAvailability } from 'support/helpers/ocr/ocr';
import { DocumentType, DocumentTypeWithVirtual, MessageV2DTO, TransactionState } from 'support/types';
import { OCRSetupModal } from 'pages/Transactions/TransactionViewPage/MessageViewPage/MessageHeader/ActionButtons/actions/components/OCRSetupModal/OCRSetupModal';

import { ActionConfig } from '../components/types';
import { AlreadyExistingMessageModal } from './components/AlreadyExistingMessageModal/AlreadyExistingMessageModal';

// Custom hook for creating document creation actions with OCR support
export const useDocumentActionWithOCR = (
  message: MessageV2DTO,
  documentType: DocumentType,
  relatedTransactions: Array<MessageV2DTO> | undefined,
  ocrAvailability: ReturnType<typeof useOCRAvailability>,
  baseConfig: Omit<ActionConfig, 'onClick' | 'documentTypeExists'>,
): ActionConfig => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSetupOCRModalOpen, setIsSetupOCRModalOpen] = useState(false);
  const [showExistingDocumentModal, setShowExistingDocumentModal] = useState(false);

  const documentTypeExists = relatedTransactions?.some(
    (transaction) =>
      mapVirtualToActualDocumentType(transaction.type as DocumentTypeWithVirtual) === documentType &&
      [TransactionState.success, TransactionState.pending].includes(transaction.state as TransactionState),
  );

  const navigateToForm = (showUploadModal?: boolean) => {
    navigate(routeToCreatePage(message.id, documentType, showUploadModal), { state: { keepBreadcrumbs: true } });
  };

  const mainActionOnClick = () => {
    if (documentTypeExists) {
      setShowExistingDocumentModal(true);
    } else {
      navigateToForm();
    }
  };

  const openSetupOCRModal = () => {
    setIsSetupOCRModalOpen(true);
  };

  const hideModal = () => {
    setShowExistingDocumentModal(false);
  };

  const confirmCreateAgain = () => {
    hideModal();
    navigateToForm();
  };

  const baseAction: ActionConfig = {
    ...baseConfig,
    label: baseConfig.label,
    documentType,
    analyticsId: baseConfig.analyticsId,
    onClick: mainActionOnClick,
    test: (message) => {
      const isDiscarded = message.state === TransactionState.dropped;
      if (isDiscarded) {
        return false;
      }
      if (baseConfig.test) {
        return baseConfig.test(message);
      }

      return true;
    },
    documentTypeExists,
    renderModal: () => (
      <>
        <OCRSetupModal
          setOpen={setIsSetupOCRModalOpen}
          open={isSetupOCRModalOpen}
          sourceMessageId={message.id}
          documentType={documentType}
        />
        <AlreadyExistingMessageModal
          isOpen={showExistingDocumentModal}
          onAction={confirmCreateAgain}
          onClose={hideModal}
        />
      </>
    ),
  };

  if (!ocrAvailability.shouldShowOCR) {
    return baseAction;
  }

  const manualAction: ActionConfig = {
    label: t('transactions:show.actionButtons.createManually'),
    onClick: () => navigateToForm(),
    analyticsId: 'transactions:create_manually',
    analyticsProperties: {
      documentType,
    },
  };

  const uploadAction: ActionConfig = {
    label: t('transactions:show.actionButtons.uploadDocument.label'),
    onClick: ocrAvailability.isBeta ? openSetupOCRModal : () => navigateToForm(true),
    analyticsId: 'transactions:upload_document',
    analyticsProperties: {
      documentType,
    },
    badge:
      ocrAvailability.isBeta && !ocrAvailability.isLoading
        ? t('transactions:show.actionButtons.uploadDocument.badge')
        : undefined,
    description:
      ocrAvailability.isBeta && !ocrAvailability.isLoading
        ? t('transactions:show.actionButtons.uploadDocument.description', {
            documentType: t(`common:messageTypes.plural.${documentType}`),
          })
        : undefined,
  };

  const subActions = [manualAction];
  if (!ocrAvailability.isBeta || ocrAvailability.hasUnattendedOcrEmail) {
    subActions.push(uploadAction);
  }

  return {
    ...baseAction,
    subActions: baseAction.subActions ?? subActions,
  };
};

export const useContainsSearchQueryAction = (action: string) => {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('action');
  return searchQuery === action;
};

/**
 * `can_resend` and the resend endpoints are scoped to the side the viewer is on, and the backend
 * resolves that side by asking about the sender first. Both resend actions read their side from
 * here so a partner that is both parties to a message only ever gets the sender's entry.
 *
 * The receiver side admits the owner of the receiver integration as well as the receiver partner,
 * matching what `/resend-as-receiver` authorizes. `/resend` is equally open to the owner of the
 * sender integration, but `MessageV2` carries no `sender_integration`, so that half cannot be
 * resolved here and such a viewer still sees no sender entry.
 */
export const resendAs = (message: MessageV2DTO, currentPartnerId: string | undefined): 'sender' | 'receiver' | null => {
  if (!currentPartnerId) {
    return null;
  }
  if (message.sender_partner?.id === currentPartnerId) {
    return 'sender';
  }
  if (
    message.receiver_partner?.id === currentPartnerId ||
    message.receiver_integration?.partner?.id === currentPartnerId
  ) {
    return 'receiver';
  }
  return null;
};

/**
 * Maps a failed mutation onto the notification subtitle its action wants to show. Statuses the
 * caller does not list — and non-backend failures — get no subtitle, leaving the generic title.
 */
export const resolveBackendErrorSubtitle = (
  error: Error,
  t: (key: string) => string,
  keysByStatus: Partial<Record<number, string>>,
): string | undefined => {
  if (!(error instanceof BackendError)) {
    return undefined;
  }
  const key = keysByStatus[error.status];

  return key ? t(key) : undefined;
};
